import {
    IHaveTPMEventStandardCalculatorProductsSynchronizeOptions,
    IHaveTPMEventStandardCalculatorSpendSynchronizeOptions,
    ITPMEventProductGroupChangedEvent,
    ITPMEventStandardCalculator,
    ITPMEventStandardCalculatorClientExtension,
} from "../../PA.TPM.Core/PA.TPM.IF.TPM.Event.Aspects";
import { TPMEventBO } from "../../PA.TPM.BusinessObjects/TPMEventBO";
import {
    WB_FinishedProductRow,
    WB_PeriodRow,
    WB_PlanRow,
    WB_TPM_EventProductGroupRow,
    WB_TPM_EventProductRow,
    WB_TPM_EventRow,
    WB_TPM_EventSpendPrdGrpRow,
    WB_TPM_EventSpendPrdRow,
    WB_TPM_EventSpendRow,
} from "../../PA.Core/PAdbSchemaRows";
import { TPMEventStandardCalculator } from "../TPMEventStandardCalculator";
import * as _ from "lodash";
import { ProductHierarchyService } from "../../PA.MD.Services/ProductHierarchyService";
import { ProductService } from "../../PA.MD.Services/ProductService";
import { ASync } from "../../PA.Core/Basic/ASync";
import { TPMEventSpend } from "../../PA.TPM.BusinessObjects/TPMEventBO.ITPMEventSpend";
import { EventStandardCalculator } from "../EventStandardCalculator";
import { PASearch } from "../../PA.Core/PASearch";
import { PricingEngine } from "../../PA.MD.Core/PricingEngine";
import { NumberUtils } from "../../PA.Core/Basic/Numbers";
import * as TPMConstants from "../../PA.TPM.Core/TPM.Constants";

let EventStandardCalculatorExtDefault: ITPMEventStandardCalculatorClientExtension = {
    get id(): string {
        return "EventStandardCalculatorExtDefaults";
    },

    BaseMetricsUpdateOptions: {
        bIncludePricing: true,
        bIncludeVolume: true,
        bRunRowCalc: true,
    },

    SpendSynchronizeOptionsDefault: {
        bIncludeSpendProductGroupsSync: true,
        bIncludeSpendProductsSync: true,
    },

    ProductGroup_RowCalcContext(aTPMEventBO: TPMEventBO, context: { [id: string]: any }): Promise<void> {
        const self: TPMEventStandardCalculator = this; // Standard Calculator

        return new Promise<void>((resolve, error) => {
            let bo = aTPMEventBO;
            let EvtType = aTPMEventBO.WB_TPMEvent.WB_EventTypeRow;

            let dctTotalDisplayQtyByPrdGrpId: { [id: number]: number } = {};
            if (EvtType.cif.PGSHPDISP_POLICY == "CalcAggSpendPGQty") {
                // Agg Spend Level
                _.forEach(
                    _.filter(bo.WB_TPMEvent.WB_TPM_EventSpendRows, (aRow) => aRow.isActive),
                    (aRow: WB_TPM_EventSpendRow) => {
                        _.forEach(
                            _.filter(aRow.WB_TPM_EventSpendPrdGrpRows, (aRow) => aRow.isActive),
                            (aRow: WB_TPM_EventSpendPrdGrpRow) => {
                                let displayQty = aRow.cif.SHIP_DISPLAY_QTY || 0;
                                if (displayQty > 0) {
                                    if (aRow.cif.WB_PRDGRP_ID in dctTotalDisplayQtyByPrdGrpId) {
                                        dctTotalDisplayQtyByPrdGrpId[aRow.cif.WB_PRDGRP_ID] += displayQty;
                                    } else {
                                        dctTotalDisplayQtyByPrdGrpId[aRow.cif.WB_PRDGRP_ID] = displayQty;
                                    }
                                }
                            },
                        );
                    },
                );
            }
            context.dctTotalDisplayQtyByPrdGrpId = dctTotalDisplayQtyByPrdGrpId;

            resolve();
        });
    },

    async OnTPMEventProductGroupPropertyChange(
        aTPMEventBO: TPMEventBO,
        change: ITPMEventProductGroupChangedEvent,
    ): Promise<void> {
        if (change.PropertyChanged !== "CNS_LIFT_PCNT_OVRD" && change.PropertyChanged !== "CNS_INCR_QTY") {
            return;
        }

        if (change.PropertyChanged === "CNS_LIFT_PCNT_OVRD" && change.Row.IncrEdit === "")
            change.Row.IncrEdit = "IncrLift";

        if (change.PropertyChanged === "CNS_INCR_QTY" && change.Row.IncrEdit === "") change.Row.IncrEdit = "IncrQty";

        if (
            (change.PropertyChanged === "CNS_LIFT_PCNT_OVRD" && change.Row.IncrEdit === "IncrQty") ||
            (change.PropertyChanged === "CNS_INCR_QTY" && change.Row.IncrEdit === "IncrLift")
        ) {
            change.Row.IncrEdit = "";
            return;
        }
        await EventStandardCalculatorExtDefault.ProductGroup_RowCalc(aTPMEventBO, change.Row, {
            PropertyChanged: change.PropertyChanged,
        });
        return Promise.resolve();
    },

    async ProductGroup_RowCalc(
        aTPMEventBO: TPMEventBO,
        aRow: WB_TPM_EventProductGroupRow,
        context: any,
    ): Promise<void> {
        const self: TPMEventStandardCalculator = this; // Standard Calculator

        return new Promise<void>((resolve, error) => {
            let bo = aTPMEventBO;
            let row = aRow.cif;
            let EvtType = aTPMEventBO.WB_TPMEvent.WB_EventTypeRow;

            async function RunAsyncProductGroup_RowCalc() {
                switch (EvtType.cif.PGBASEPRICE_POLICY) {
                    case "UserInput":
                    case "PricingEngine":
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "BASE_PRICE",
                            context,
                        ]);
                        break;
                }

                switch (EvtType.cif.PGEDLPPRICE_POLICY) {
                    case "UserInput":
                    case "EDLPEvent": // Handled in parent
                        break;

                    case "CalcDiscountAmt":
                        row.EDLP_PRICE = Math.max(0, row.BASE_PRICE || 0 - Number(row.DISCOUNT_AMT) || 0);
                        break;

                    case "CalcDiscountPcnt":
                        row.EDLP_PRICE = Math.max(0, (row.BASE_PRICE || 0) * (1 - (Number(row.DISCOUNT_PCNT) || 0)));
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "EDLP_PRICE",
                            context,
                        ]);
                        break;
                }

                let startPrice = row.BASE_PRICE || 0;
                if (row.EDLP_PRICE > 0) {
                    startPrice = Number(row.EDLP_PRICE);
                }

                switch (EvtType.cif.PGPROMOTEDPRICE_POLICY) {
                    case "UserInput":
                        break;

                    case "CalcDiscountAmt":
                        row.PROMOTED_PRICE = Math.max(0, startPrice || 0 - Number(row.DISCOUNT_AMT) || 0);
                        break;

                    case "CalcDiscountPcnt":
                        row.PROMOTED_PRICE = Math.max(
                            0,
                            (row.BASE_PRICE || 0) * (1 - (Number(row.DISCOUNT_PCNT) || 0)),
                        );
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "PROMOTED_PRICE",
                            context,
                        ]);
                        break;
                }

                switch (EvtType.cif.PGDISCOUNTAMT_POLICY) {
                    case "UserInput":
                        break;

                    case "CalcPromoEDLPBase":
                        row.DISCOUNT_AMT = startPrice - Number(row.PROMOTED_PRICE);
                        break;

                    case "CalcEDLPBase":
                        row.DISCOUNT_AMT = Number(row.EDLP_PRICE) - Number(row.PROMOTED_PRICE);
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "DISCOUNT_AMT",
                            context,
                        ]);
                        break;
                }

                switch (EvtType.cif.PGDISCOUNTPCNT_POLICY) {
                    case "UserInput":
                        break;

                    case "CalcPromoEDLPBase":
                        if (startPrice > 0) {
                            row.DISCOUNT_PCNT = Number(row.DISCOUNT_AMT) / startPrice;
                        } else {
                            row.DISCOUNT_PCNT = 0;
                        }
                        break;

                    case "CalcEDLPBase":
                        if (row.BASE_PRICE > 0) {
                            row.DISCOUNT_PCNT = Number(row.DISCOUNT_AMT) / row.BASE_PRICE;
                        } else {
                            row.DISCOUNT_PCNT = 0;
                        }
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "DISCOUNT_PCNT",
                            context,
                        ]);
                        break;
                }

                let coverageFactor = 1;
                if (EvtType.cif.COVERAGEFACTOR_SHOW_IND === "1") {
                    switch (EvtType.cif.COVERAGEFACTOR_LEVEL) {
                        case "Event":
                            coverageFactor = Number(bo.WB_TPMEvent.cif.COVERAGE_FACTOR);
                            break;
                        case "ProductGroup":
                            coverageFactor = Number(row.COVERAGE_FACTOR);
                            break;
                    }
                }

                // No Base Policy
                row.CNS_BASE_QTY = coverageFactor * row.CNS_PLAN_BASE_QTY;

                // Do Lift Policy
                switch (EvtType.cif.PGCNSLIFTPCNT_POLICY) {
                    case "NoModel":
                        row.CNS_LIFT_PCNT = 0;
                        break;

                    case "CalcMerchModel":
                        row.CNS_LIFT_PCNT = 0;
                        break;

                    case "CalcPriceModel":
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "CNS_LIFT_PCNT",
                            context,
                        ]);
                        break;
                }

                let liftPcnt = 0;
                if (EvtType.bPGCnsLiftPcntShowInd) {
                    liftPcnt = Number(row.CNS_LIFT_PCNT);
                }

                if (row.CNS_LIFT_PCNT_OVRD > 0 && EvtType.bPGCnsLiftPcntOvrdShowInd) {
                    liftPcnt = Number(row.CNS_LIFT_PCNT_OVRD);
                }

                switch (EvtType.cif.PGCNSEDLP_POLICY) {
                    case "UserInput":
                        break;

                    case "CalcLiftBase":
                        row.CNS_EDLP_QTY = row.CNS_BASE_QTY * liftPcnt;
                        break;

                    case "EDLPEvent":
                        row.CNS_EDLP_QTY = coverageFactor * row.CNS_PLAN_EDLP_QTY;
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "CNS_EDLP_QTY",
                            context,
                        ]);
                        break;
                }
                row.CNS_BASEANDEDLP_QTY = Number(row.CNS_BASE_QTY) + Number(row.CNS_EDLP_QTY);

                switch (EvtType.cif.PGCNSINCR_POLICY) {
                    //   case 'UserInput':
                    //       break;

                    case "CalcLiftBase":
                        if (row.IncrEdit === "IncrQty") {
                            if (row.CNS_BASE_QTY != 0)
                                row.CNS_LIFT_PCNT_OVRD = Number(row.CNS_INCR_QTY) / Number(row.CNS_BASE_QTY);
                            else row.CNS_LIFT_PCNT_OVRD = 0;
                        }
                        if (row.IncrEdit === "IncrLift") {
                            row.CNS_INCR_QTY = Number(row.CNS_BASE_QTY) * liftPcnt;
                        }
                        break;

                    case "CalcLiftEDLP":
                        if (row.IncrEdit === "IncrQty") {
                            if (row.CNS_BASEANDEDLP_QTY != 0)
                                row.CNS_LIFT_PCNT_OVRD = Number(row.CNS_INCR_QTY) / row.CNS_BASEANDEDLP_QTY;
                            else row.CNS_LIFT_PCNT_OVRD = 0;
                        }
                        if (row.IncrEdit === "IncrLift") {
                            row.CNS_INCR_QTY = Number(row.CNS_BASEANDEDLP_QTY) * liftPcnt;
                        }
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "CNS_INCR_QTY",
                            context,
                        ]);
                        break;
                }

                if (row.CNS_LIFT_PCNT_OVRD > 0 && EvtType.bPGCnsLiftPcntOvrdShowInd) {
                    liftPcnt = Number(row.CNS_LIFT_PCNT_OVRD);
                }

                if (EvtType.cif.PGCNSEDLP_POLICY == "CalcLiftBase") {
                    row.CNS_EDLP_QTY = Number(row.CNS_BASE_QTY) * liftPcnt;
                }
                row.CNS_BASEANDEDLP_QTY = Number(row.CNS_BASE_QTY) + Number(row.CNS_EDLP_QTY);

                switch (EvtType.cif.PGCNSCANN_POLICY) {
                    case "UserInput":
                        break;

                    case "CalcCann":
                        // TODO: Calculate Cannibalization based on Modeling/ Overlap
                        row.CNS_CANN_QTY = 0;
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "CNS_CANN_QTY",
                            context,
                        ]);
                        break;
                }

                switch (EvtType.cif.PGCNSTOTAL_POLICY) {
                    case "CalcTotal":
                        row.CNS_TOTAL_QTY =
                            Number(row.CNS_BASE_QTY) +
                            Number(row.CNS_EDLP_QTY) +
                            Number(row.CNS_INCR_QTY) +
                            Number(row.CNS_CANN_QTY);
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "CNS_TOTAL_QTY",
                            context,
                        ]);
                        break;
                }

                // No Shp Base Policy
                row.SHIP_BASE_QTY = coverageFactor * row.SHIP_PLAN_BASE_QTY;

                switch (EvtType.cif.PGSHPEDLP_POLICY) {
                    case "UserInput":
                        break;

                    case "CalcLiftBase":
                        row.SHIP_EDLP_QTY = row.SHIP_BASE_QTY * liftPcnt;
                        break;

                    case "EDLPEvent":
                        row.SHIP_EDLP_QTY = coverageFactor * row.SHIP_PLAN_EDLP_QTY;
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "SHIP_EDLP_QTY",
                            context,
                        ]);
                        break;
                }

                row.SHIP_BASEANDEDLP_QTY = row.SHIP_BASE_QTY + row.SHIP_EDLP_QTY;

                switch (EvtType.cif.PGSHPINCR_POLICY) {
                    case "UserInput":
                        break;

                    case "CalcCopyCnsIncr":
                        row.SHIP_INCR_QTY = Number(row.CNS_INCR_QTY);
                        break;

                    case "CalcLiftEDLP":
                        row.SHIP_INCR_QTY = liftPcnt * (row.SHIP_BASE_QTY + row.SHIP_EDLP_QTY);
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "SHIP_INCR_QTY",
                            context,
                        ]);
                        break;
                }

                switch (EvtType.cif.PGSHPFWDBUY_POLICY) {
                    case "UserInput":
                        break;

                    case "CalcFwdBuyPcnt":
                        row.SHIP_FORWARD_BUY_QTY = Number(row.CNS_FORWARD_BUY_PCNT) * Number(row.CNS_TOTAL_QTY);
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "SHIP_FORWARD_BUY_QTY",
                            context,
                        ]);
                        break;
                }

                switch (EvtType.cif.PGSHPADJ_POLICY) {
                    case "CalcPlanAdj":
                        row.SHIP_ADJ_QTY = coverageFactor * (Number(row.SHIP_PLAN_ADJ_QTY) || 0);
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "SHIP_ADJ_QTY",
                            context,
                        ]);
                        break;
                }

                switch (EvtType.cif.PGSHPTOTAL_POLICY) {
                    case "CalcTotal":
                        row.SHIP_TOTAL_QTY = NumberUtils.round(
                            _.toNumber(row.SHIP_BASE_QTY) +
                                _.toNumber(row.SHIP_EDLP_QTY) +
                                _.toNumber(row.SHIP_INCR_QTY) +
                                Number(row.SHIP_ADJ_QTY) +
                                _.toNumber(row.SHIP_ADJ_QTY),
                            6,
                        );
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "SHIP_TOTAL_QTY",
                            context,
                        ]);
                        break;
                }

                switch (EvtType.cif.PGSHPDISP_POLICY) {
                    case "UserInput":
                        break;

                    case "CalcAggSpendPGQty":
                        let dctTotalDisplayQtyByPrdGrpId = context.dctTotalDisplayQtyByPrdGrpId || {};
                        if (row.WB_PRDGRP_ID in dctTotalDisplayQtyByPrdGrpId) {
                            row.SHIP_DISPLAY_QTY = dctTotalDisplayQtyByPrdGrpId[row.WB_PRDGRP_ID] || 0;
                        } else {
                            row.SHIP_DISPLAY_QTY = 0;
                        }
                        break;

                    case "CalcCustom":
                        await self._handleExtensionExecution("ProductGroup_RowCalcCustom", [
                            self,
                            bo,
                            aRow,
                            "SHIP_DISPLAY_QTY",
                            context,
                        ]);
                        break;
                }
            }

            RunAsyncProductGroup_RowCalc()
                .then((ignore) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    },

    async Price_Event_Products(aTPMEventBO: TPMEventBO): Promise<void> {
        let evtRow: WB_TPM_EventRow = aTPMEventBO.WB_TPMEvent;

        if (_.isUndefined(evtRow)) return;

        let arrEvtPrds: WB_TPM_EventProductRow[] = _.filter(evtRow.WB_TPM_EventProductRows, (aRow) => aRow.isActive);

        let planRow: WB_PlanRow = evtRow.WB_PlanRow;
        let pricingEnginedId: number = planRow.WB_PlanTypeRow.WB_BusinessUnitRow.cif.WB_PRICINGENGINE_ID;
        let custId: number = evtRow.cif.WB_CUST_ID_PLANNING;

        for (let i = 0; i < arrEvtPrds.length; i++) {
            let evtPrd: WB_TPM_EventProductRow = arrEvtPrds[i];
            let wbPrdId: number = evtPrd.cif.WB_PROD_ID;

            let grossListPrice = await PricingEngine.getProductListPriceByDate(
                "GrossList",
                pricingEnginedId,
                wbPrdId,
                custId,
                evtPrd.WB_TPM_Event.ShipmentStartDt,
            );
            let netListPrice = await PricingEngine.getProductListPriceByDate(
                "NetList",
                pricingEnginedId,
                wbPrdId,
                custId,
                evtPrd.WB_TPM_Event.ShipmentStartDt,
            );
            evtPrd.cif.GROSS_LIST_PRICE = _.isUndefined(grossListPrice) ? undefined : grossListPrice;
            evtPrd.cif.NET_LIST_PRICE = !_.isUndefined(netListPrice) ? netListPrice : undefined;
        }

        return Promise.resolve();
    },

    async _Price_EventSpend_Products(aTPMEventBO: TPMEventBO, ProductRows: WB_TPM_EventSpendPrdRow[]): Promise<void> {
        let evtRow: WB_TPM_EventRow = aTPMEventBO.WB_TPMEvent;

        if (_.isUndefined(evtRow)) return;

        let arrEvtPrds: WB_TPM_EventSpendPrdRow[] = ProductRows;

        let planRow: WB_PlanRow = evtRow.WB_PlanRow;
        let pricingEnginedId: number = planRow.WB_PlanTypeRow.WB_BusinessUnitRow.cif.WB_PRICINGENGINE_ID;
        let custId: number = evtRow.cif.WB_CUST_ID_PLANNING;

        for (let i = 0; i < arrEvtPrds.length; i++) {
            let evtPrd: WB_TPM_EventSpendPrdRow = arrEvtPrds[i];
            let wbPrdId: number = evtPrd.cif.WB_PROD_ID;

            let grossListPrice = await PricingEngine.getProductListPriceByDate(
                "GrossList",
                pricingEnginedId,
                wbPrdId,
                custId,
                evtPrd.WB_TPM_EventSpendRow.ShipmentStartDt,
            );
            let netListPrice = await PricingEngine.getProductListPriceByDate(
                "NetList",
                pricingEnginedId,
                wbPrdId,
                custId,
                evtPrd.WB_TPM_EventSpendRow.ShipmentStartDt,
            );
            evtPrd.cif.GROSS_LIST_PRICE = _.isUndefined(grossListPrice) ? undefined : grossListPrice;
            evtPrd.cif.NET_LIST_PRICE = !_.isUndefined(netListPrice) ? netListPrice : undefined;
        }

        return Promise.resolve();
    },

    SynchronizeEventProducts(
        aTPMEventBO: TPMEventBO,
        context: any,
        opts: IHaveTPMEventStandardCalculatorProductsSynchronizeOptions,
    ): Promise<void> {
        const self: TPMEventStandardCalculator = this; // Standard Calculator

        return new Promise<void>((resolve, error) => {
            let bo = aTPMEventBO;
            let EvtType = aTPMEventBO.WB_TPMEvent.WB_EventTypeRow;

            async function RunAsyncSynchronizeEventProducts() {
                let arrRowsToSync: WB_TPM_EventProductGroupRow[] = [];
                let arrActivePGIds: number[] = [];
                let bFindOrphans = false;

                await EventStandardCalculatorExtDefault._ComponentPPG(aTPMEventBO);

                if (!_.isEmpty(opts.WB_TPM_EventProductGroupRows)) {
                    arrRowsToSync = opts.WB_TPM_EventProductGroupRows;
                    bFindOrphans = true;
                } else {
                    arrRowsToSync = bo.WB_TPMEvent.WB_TPM_EventProductGroupRows;
                }

                arrRowsToSync = _.filter(arrRowsToSync, (aRow: WB_TPM_EventProductGroupRow) => aRow.isActive);

                for (let i = 0; i < arrRowsToSync.length; i++) {
                    let curRow = arrRowsToSync[i];
                    let arrRowProdIds = await ProductHierarchyService.getChildrenByPeriodWbID(
                        bo.WB_TPMEvent.WB_PlanRow.WB_PlanTypeRow.cif.WB_PRDH_ID,
                        curRow.cif.WB_PRDGRP_ID,
                        bo.WB_TPMEvent.WB_PlanRow.cif.WB_PERIOD_ID,
                    );

                    if (bo.WB_TPMEvent.WB_EventTypeRow.cif.FINISHED_PRODUCT_POLICY == "AddRelated") {
                        let relatedFinishedProducts = await ProductService.getRelatedFinishedProducts(arrRowProdIds);
                        arrRowProdIds.push.apply(arrRowProdIds, relatedFinishedProducts);
                        arrRowProdIds = _.uniq(arrRowProdIds);
                    }

                    let existingProductRows = _.filter(
                        bo.WB_TPMEvent.WB_TPM_EventProductRows,
                        (aRow: WB_TPM_EventProductRow) =>
                            aRow.isActive && aRow.cif.PARENT_ROW_ID == curRow.cif.WB_TEVTPG_ID,
                    );

                    // Determine Missing Products
                    let arrExistingWBProdIds = _.map(
                        existingProductRows,
                        (aRow: WB_TPM_EventProductRow) => aRow.cif.WB_PROD_ID,
                    );

                    let arrMissingWBProdIds = _.difference(arrRowProdIds, arrExistingWBProdIds);
                    let arrDeleteWBProdIds = _.difference(arrExistingWBProdIds, arrRowProdIds);

                    let deleteRows = _.filter(existingProductRows, (aRow: WB_TPM_EventProductRow) =>
                        _.includes(arrDeleteWBProdIds, aRow.cif.WB_PROD_ID),
                    );
                    let deleteRowIds = _.map(deleteRows, (aRow: WB_TPM_EventProductRow) => aRow.cif.WB_TEVTPRD_ID);

                    if (!_.isEmpty(deleteRowIds)) {
                        await bo.removeTPMEventProductRow(deleteRowIds, { bWithoutRefresh: true });
                    }

                    if (!_.isEmpty(arrMissingWBProdIds)) {
                        await bo.addTPMEventProduct(arrMissingWBProdIds, {
                            bWithoutRefresh: true,
                            ParentRowId: curRow.cif.WB_TEVTPG_ID,
                        });
                    }
                }

                arrActivePGIds = _.map(
                    _.filter(arrRowsToSync, (aRow: WB_TPM_EventProductGroupRow) => aRow.isActive),
                    (aPG) => {
                        return aPG.cif.WB_TEVTPG_ID;
                    },
                );
                let arrDelProdIds: number[] = [];
                arrDelProdIds = _.map(
                    _.filter(
                        bo.WB_TPMEvent.WB_TPM_EventProductRows,
                        (aRow: WB_TPM_EventProductRow) =>
                            aRow.cif.PARENT_ROW_ID != 0 && !_.includes(arrActivePGIds, aRow.cif.PARENT_ROW_ID),
                    ),
                    (aProd) => {
                        return aProd.cif.WB_TEVTPRD_ID;
                    },
                );
                await bo.removeTPMEventProductRow(arrDelProdIds, { bWithoutRefresh: true });

                if (bFindOrphans) {
                    let RowWBIds = _.map(arrRowsToSync, (aRow) => aRow.cif.WB_TEVTPG_ID);
                    let orphaned: WB_TPM_EventProductRow[] = _.filter(
                        bo.WB_TPMEvent.WB_TPM_EventProductRows,
                        (aRow: WB_TPM_EventProductRow) =>
                            aRow.isActive &&
                            !_.includes(RowWBIds, aRow.cif.PARENT_ROW_ID) &&
                            aRow.cif.PARENT_ROW_ID > 0,
                    );

                    let orhpanedRowWBIds = _.map(orphaned, (aRow) => aRow.cif.WB_TEVTPRD_ID);
                    if (!_.isEmpty(orhpanedRowWBIds)) {
                        await bo.removeTPMEventProductRow(orhpanedRowWBIds, { bWithoutRefresh: true });
                    }
                }
            }

            RunAsyncSynchronizeEventProducts()
                .then((ignore) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    },

    _ComponentPPG(aTPMEventBO: TPMEventBO): Promise<void> {
        const self: TPMEventStandardCalculator = this; // Standard Calculator

        return new Promise<void>((resolve, error) => {
            let arrWBProdIds: number[] = _.map(
                _.filter(
                    aTPMEventBO.WB_TPMEvent.WB_TPM_EventProductRows,
                    (aRow) => aRow.isActive && aRow.cif.ROW_TYPE == "Display",
                ),
                (aRow: WB_TPM_EventProductRow) => {
                    return aRow.cif.WB_PROD_ID;
                },
            );

            let arrExistingProdGrpIds: number[] = _.map(
                aTPMEventBO.WB_TPMEvent.WB_TPM_EventProductGroupRows,
                (aRow: WB_TPM_EventProductGroupRow) => {
                    return aRow.cif.WB_PRDGRP_ID;
                },
            );
            let arrDisplayProdGrps: WB_TPM_EventProductGroupRow[] =
                aTPMEventBO.WB_TPMEvent.WB_TPM_EventProductGroupRows.filter((item) => item.cif.DISPLAY_COMP_IND == "1");
            let arrDisplayProdGrpIds: number[] = _.map(arrDisplayProdGrps, (aRow: WB_TPM_EventProductGroupRow) => {
                return aRow.cif.WB_PRDGRP_ID;
            });
            let arrWBProdGrpIds: number[] = [];

            let bChanged: boolean = false;

            PASearch.find(
                "vUI_TPMEvent_DisplayProducts",
                {
                    WB_PROD_ID: { inq: arrWBProdIds },
                    EFFECTIVE_START_DT: { lte: aTPMEventBO.WB_TPMEvent.WB_PlanRow.WB_PeriodRow.cif.PERIOD_END_DT },
                    EFFECTIVE_END_DT: { gte: aTPMEventBO.WB_TPMEvent.WB_PlanRow.WB_PeriodRow.cif.PERIOD_START_DT },
                },
                {
                    WB_PROD_ID: { type: "number" },
                    EFFECTIVE_START_DT: { type: "string" },
                    EFFECTIVE_END_DT: { type: "string" },
                },
                ["WB_PRDGRP_ID_PROMOTED_GROUP"],
                0,
                ["WB_PRDGRP_ID_PROMOTED_GROUP"],
            )
                .then((arrResults: any[]) => {
                    arrWBProdGrpIds = _.map(arrResults, (aResult) => {
                        return aResult.WB_PRDGRP_ID_PROMOTED_GROUP;
                    });
                    var arrProdGrpIdsToBeAdded: number[] = arrWBProdGrpIds.filter(
                        (item) => !arrExistingProdGrpIds.includes(item),
                    );
                    var arrProdGrpIdsToBeDeleted: number[] = arrExistingProdGrpIds.filter(
                        (item) => !arrWBProdGrpIds.includes(item) && arrDisplayProdGrpIds.includes(item),
                    );
                    var arrTPMEventPrdGrpWBIdsToBeDel: number[] = _.map(
                        arrDisplayProdGrps.filter((item) => arrProdGrpIdsToBeDeleted.includes(item.cif.WB_PRDGRP_ID)),
                        (aRow: WB_TPM_EventProductGroupRow) => {
                            return aRow.cif.WB_TEVTPG_ID;
                        },
                    );

                    var arrIntersectionProdGrpIds: number[] = arrWBProdGrpIds.filter((item) =>
                        arrExistingProdGrpIds.includes(item),
                    );

                    bChanged = arrProdGrpIdsToBeAdded.length > 0 || arrTPMEventPrdGrpWBIdsToBeDel.length > 0;

                    _.forEach(aTPMEventBO.WB_TPMEvent.WB_TPM_EventProductGroupRows, (aRow) => {
                        if (!aRow.isActive) {
                            return;
                        }
                        if (arrIntersectionProdGrpIds.includes(aRow.cif.WB_PRDGRP_ID)) aRow.cif.DISPLAY_COMP_IND = "1";
                    });

                    async function executePrdGrpSync() {
                        if (arrProdGrpIdsToBeAdded.length > 0)
                            await aTPMEventBO.addTPMEventProductGroup(arrProdGrpIdsToBeAdded, {
                                bWithoutRefresh: true,
                                bDisplayCompPrdGrp: true,
                            });

                        if (arrProdGrpIdsToBeDeleted.length > 0)
                            await aTPMEventBO.removeTPMEventProductGroup(arrTPMEventPrdGrpWBIdsToBeDel, {
                                bWithoutRefresh: true,
                            });
                    }

                    return executePrdGrpSync();

                    /*if(arrProdGrpIdsToBeAdded.length > 0)
                    return aTPMEventBO.addTPMEventProductGroup(arrProdGrpIdsToBeAdded, {bWithoutRefresh: true, bDisplayCompPrdGrp: true})
                else
                    resolve()*/
                })
                .then((ignore) => {
                    if (bChanged) aTPMEventBO.TPMEventProductGroupCollectionChanged.next(aTPMEventBO);
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    },

    EventSpendSync(
        aTPMEventBO: TPMEventBO,
        aTPMEventSpend: TPMEventSpend,
        context: any,
        opts: IHaveTPMEventStandardCalculatorSpendSynchronizeOptions,
    ): Promise<void> {
        const self: EventStandardCalculator = this;

        return new Promise<void>((resolve, error) => {
            EventStandardCalculatorExtDefault._EventSpendSync_Headers(aTPMEventBO, aTPMEventSpend, context, opts)
                .then((ignore: any) => {
                    if (opts.bIncludeSpendProductGroupsSync) {
                        return EventStandardCalculatorExtDefault._EventSpendSync_ProductGroups(
                            aTPMEventBO,
                            aTPMEventSpend,
                            context,
                            opts,
                        );
                    } else {
                        return ASync.resolve();
                    }
                })
                .then((ignore: any) => {
                    if (opts.bIncludeSpendProductsSync) {
                        return EventStandardCalculatorExtDefault._EventSpendSync_Products(
                            aTPMEventBO,
                            aTPMEventSpend,
                            context,
                            opts,
                        );
                    } else {
                        return ASync.resolve();
                    }
                })
                .then((ignore: any) => {
                    resolve();
                })
                .catch((err: any) => {
                    error(err);
                });
        });
    },

    _EventSpendSync_Headers(
        aTPMEventBO: TPMEventBO,
        aTPMEventSpend: TPMEventSpend,
        context: any,
        opts: IHaveTPMEventStandardCalculatorSpendSynchronizeOptions,
    ): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            let evtRow = aTPMEventBO.WB_TPMEvent;
            let evtType = evtRow.WB_EventTypeRow;
            let spendRow = aTPMEventSpend.TPMEventSpendRow;
            let spendType = spendRow.WB_TPM_EventSpendTypeRow;

            switch (spendType.cif.INSTOREDATES_POLICY) {
                case "SyncToEvent":
                    spendRow.InStoreStartDt = evtRow.InStoreStartDt;
                    spendRow.InStoreEndDt = evtRow.InStoreEndDt;
                    break;
            }

            switch (spendType.cif.SHIPMENTDATES_POLICY) {
                case "SyncToEvent":
                    spendRow.ShipmentStartDt = evtRow.ShipmentStartDt;
                    spendRow.ShipmentEndDt = evtRow.ShipmentEndDt;
                    break;
            }

            switch (spendType.cif.DISTRIBUTIONDATES_POLICY) {
                case "SyncToEvent":
                    spendRow.DistributionStartDt = evtRow.DistributionStartDt;
                    spendRow.DistributionEndDt = evtRow.DistributionEndDt;
                    break;
            }

            switch (spendType.cif.ORDERDATES_POLICY) {
                case "SyncToEvent":
                    spendRow.OrderStartDt = evtRow.OrderStartDt;
                    spendRow.OrderEndDt = evtRow.OrderEndDt;
                    break;
            }

            resolve();
        });
    },

    _EventSpendSync_ProductGroups(
        aTPMEventBO: TPMEventBO,
        aTPMEventSpend: TPMEventSpend,
        context: any,
        opts: IHaveTPMEventStandardCalculatorSpendSynchronizeOptions,
    ): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            let evtRow = aTPMEventBO.WB_TPMEvent;
            let evtType = evtRow.WB_EventTypeRow;
            let spendRow = aTPMEventSpend.TPMEventSpendRow;
            let spendType = spendRow.WB_TPM_EventSpendTypeRow;

            let arrProductGroupIds: number[] = [];
            let arrEvtSPGRowsExisting = _.filter(spendRow.WB_TPM_EventSpendPrdGrpRows, (aRow) => aRow.isActive);
            let arrProductGroupIdsExisting: number[] = _.map(arrEvtSPGRowsExisting, (aRow) => aRow.cif.WB_PRDGRP_ID);
            let arrProductGroupsIdsEvent: number[] = _.map(
                _.filter(evtRow.WB_TPM_EventProductGroupRows, (aRow) => aRow.isActive),
                (aRow) => aRow.cif.WB_PRDGRP_ID,
            );
            if (spendType.cif.PRODUCTGROUP_SYNC_POLICY == "All") {
                arrProductGroupIds = arrProductGroupsIdsEvent;
            }

            let arrOpenStockPrdGrpIds: number[] = _.map(
                _.filter(evtRow.WB_TPM_EventProductGroupRows, (aRow) => aRow.cif.OPENSTOCK_IND == "1"),
                (aRow) => aRow.cif.WB_PRDGRP_ID,
            );
            let arrDisplayCompPrdGrpIds: number[] = _.map(
                _.filter(evtRow.WB_TPM_EventProductGroupRows, (aRow) => aRow.cif.DISPLAY_COMP_IND == "1"),
                (aRow) => aRow.cif.WB_PRDGRP_ID,
            );

            switch (spendType.cif.PRODUCTGROUP_SYNC_POLICY) {
                case "Open_Stock":
                    arrProductGroupIds = arrOpenStockPrdGrpIds;
                    break;

                case "Display_Component":
                    arrProductGroupIds = arrDisplayCompPrdGrpIds;
                    break;

                case "All":
                    arrProductGroupIds = arrProductGroupsIdsEvent;
                    break;
            }

            let arrProductGroupIdsMissing: number[] = _.difference(arrProductGroupIds, arrProductGroupIdsExisting);
            let arrProductGroupIdsExtra: number[] = _.difference(arrProductGroupIdsExisting, arrProductGroupIds);

            let addPromise;
            if (!_.isEmpty(arrProductGroupIdsMissing)) {
                let bIncludeDefault: boolean = true;
                if (
                    aTPMEventSpend.PrimaryWorkflowProcessor.CurrentState.StatusTypeIsPostApproval &&
                    aTPMEventSpend.TPMEventSpendRow.cif.PAYMENT_METHOD == TPMConstants.PaymentMethod_OffInvoice
                )
                    bIncludeDefault = false;
                addPromise = aTPMEventSpend.addTPMEventSpendProductGroup(arrProductGroupIdsMissing, {
                    bInclude: bIncludeDefault,
                });
            } else {
                addPromise = ASync.resolve();
            }
            addPromise
                .then((ignore) => {
                    if (!_.isEmpty(arrProductGroupIdsExtra)) {
                        let removeRows = _.filter(arrEvtSPGRowsExisting, (aRow) =>
                            _.includes(arrProductGroupIdsExtra, aRow.cif.WB_PRDGRP_ID),
                        );
                        let removeIds = _.map(removeRows, (aRow) => aRow.cif.WB_TEVTSPG_ID);
                        return aTPMEventSpend.removeTPMEventSpendProductGroupRow(removeIds, {});
                    } else {
                        return ASync.resolve();
                    }
                })
                .then((ignore) => {
                    // Get Pricing from Event Product Group.. if not set
                    let evtPGRowByPGId = _.keyBy(
                        _.filter(aTPMEventBO.WB_TPMEvent.WB_TPM_EventProductGroupRows, (aRow) => aRow.isActive),
                        (aRow) => aRow.cif.WB_PRDGRP_ID,
                    );

                    _.forEach(aTPMEventSpend.TPMEventSpendRow.WB_TPM_EventSpendPrdGrpRows, (aRow) => {
                        if (!aRow.isActive) {
                            return;
                        }
                        let evtPGRow = evtPGRowByPGId[aRow.cif.WB_PRDGRP_ID];
                        if (_.isEmpty(evtPGRow)) {
                            return;
                        }
                        //  if (aRow.cif.GROSS_LIST_PRICE == 0) {
                        aRow.cif.GROSS_LIST_PRICE = evtPGRow.cif.GROSS_LIST_PRICE;
                        //  }

                        //  if (aRow.cif.NET_LIST_PRICE == 0) {
                        aRow.cif.NET_LIST_PRICE = evtPGRow.cif.NET_LIST_PRICE;
                        //  }

                        aRow.cif.OPENSTOCK_IND = evtPGRow.cif.OPENSTOCK_IND;
                        aRow.cif.DISPLAY_COMP_IND = evtPGRow.cif.DISPLAY_COMP_IND;
                    });

                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    },

    _EventSpendSync_Products(
        aTPMEventBO: TPMEventBO,
        aTPMEventSpend: TPMEventSpend,
        context: any,
        opts: IHaveTPMEventStandardCalculatorSpendSynchronizeOptions,
    ): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            let evtRow = aTPMEventBO.WB_TPMEvent;
            let evtType = evtRow.WB_EventTypeRow;
            let spendRow = aTPMEventSpend.TPMEventSpendRow;
            let spendType = spendRow.WB_TPM_EventSpendTypeRow;

            let dctProductIdsByRowType: { [id: string]: number[] } = {};
            let arrEvtSPrdRowsExisting = _.filter(spendRow.WB_TPM_EventSpendPrdRows, (aRow) => aRow.isActive);
            let arrProductIdsExisting: number[] = _.map(arrEvtSPrdRowsExisting, (aRow) => aRow.cif.WB_PROD_ID);
            let arrProductRowsEvent = _.filter(evtRow.WB_TPM_EventProductRows, (aRow) => aRow.isActive);

            let dctEventProductsByProdId = _.keyBy(
                evtRow.WB_TPM_EventProductRows,
                (aRow: WB_TPM_EventProductRow) => aRow.cif.WB_PROD_ID,
            );

            let bAllPolicy = spendType.cif.PRODUCT_SYNC_POLICY == "All";
            let bConsumerPolicy = spendType.cif.PRODUCT_SYNC_POLICY == "ConsumerProducts";
            let bFinishedPolicy = spendType.cif.PRODUCT_SYNC_POLICY == "FinishedProducts";
            let bDisplayPolicy = spendType.cif.PRODUCT_SYNC_POLICY == "Displays";

            if (bAllPolicy || bConsumerPolicy) {
                let arrConsumer = _.filter(arrProductRowsEvent, (aRow) => aRow.cif.ROW_TYPE == "Consumer");
                dctProductIdsByRowType["Consumer"] = _.map(arrConsumer, (aRow) => aRow.cif.WB_PROD_ID);
            }

            if (bAllPolicy || bFinishedPolicy) {
                let arrFinished = _.filter(arrProductRowsEvent, (aRow) => aRow.cif.ROW_TYPE == "Finished");
                dctProductIdsByRowType["Finished"] = _.map(arrFinished, (aRow) => aRow.cif.WB_PROD_ID);
            }

            if (bAllPolicy || bDisplayPolicy) {
                let arrDisplay = _.filter(arrProductRowsEvent, (aRow) => aRow.cif.ROW_TYPE == "Display");
                dctProductIdsByRowType["Display"] = _.map(arrDisplay, (aRow) => aRow.cif.WB_PROD_ID);
            }

            let dctProductIdsByRowTypeExisting: { [id: string]: number[] } = {};
            _.forEach(arrEvtSPrdRowsExisting, (aRow) => {
                if (aRow.cif.ROW_TYPE in dctProductIdsByRowTypeExisting) {
                    dctProductIdsByRowTypeExisting[aRow.cif.ROW_TYPE].push(aRow.cif.WB_PROD_ID);
                } else {
                    dctProductIdsByRowTypeExisting[aRow.cif.ROW_TYPE] = [aRow.cif.WB_PROD_ID];
                }
            });

            async function RunSpendProductSync() {
                // Determine the list of Products to exclude..
                let excludedProducts: number[] = [];
                let excludedPGs: number[] = [];

                _.forEach(aTPMEventSpend.TPMEventSpendRow.WB_TPM_EventSpendPrdGrpRows, (aRow) => {
                    if (!aRow.isActive) {
                        return;
                    }
                    if (aRow.cif.INCLUDE_IND == "0") {
                        excludedPGs.push(aRow.cif.WB_PRDGRP_ID);
                    }
                });

                if (!_.isEmpty(excludedPGs)) {
                    for (let aWbPrdGrpId of excludedPGs) {
                        let arrRowProdIds = await ProductHierarchyService.getChildrenByPeriodWbID(
                            evtRow.WB_PlanRow.WB_PlanTypeRow.cif.WB_PRDH_ID,
                            aWbPrdGrpId,
                            evtRow.WB_PlanRow.cif.WB_PERIOD_ID,
                        );

                        if (evtRow.WB_EventTypeRow.cif.FINISHED_PRODUCT_POLICY == "AddRelated") {
                            let relatedFinishedProducts =
                                await ProductService.getRelatedFinishedProducts(arrRowProdIds);
                            arrRowProdIds.push.apply(arrRowProdIds, relatedFinishedProducts);
                            arrRowProdIds = _.uniq(arrRowProdIds);
                        }
                        excludedProducts.push.apply(excludedProducts, arrRowProdIds);
                    }
                }

                // For Each Row Type, Sync Up
                let rowTypes = _.uniq(_.union(_.keys(dctProductIdsByRowType), _.keys(dctProductIdsByRowTypeExisting)));
                for (let typeIdx = 0; typeIdx < rowTypes.length; typeIdx++) {
                    let rowType = rowTypes[typeIdx];
                    let productIds = dctProductIdsByRowType[rowType] || [];
                    let productIdsExisting = dctProductIdsByRowTypeExisting[rowType] || [];

                    // remove excluded products..
                    productIds = _.difference(productIds, excludedProducts);

                    let productIdsMissing = _.difference(productIds, productIdsExisting);
                    let productIdsExtra = _.difference(productIdsExisting, productIds);

                    if (!_.isEmpty(productIdsMissing)) {
                        await aTPMEventSpend.addTPMEventSpendProduct(productIdsMissing, { RowType: rowType });
                    }

                    if (!_.isEmpty(productIdsExtra)) {
                        let arrExtra = _.filter(arrEvtSPrdRowsExisting, (aRow) =>
                            _.includes(productIdsExtra, aRow.cif.WB_PROD_ID),
                        );
                        let arrExtraIds = _.map(arrExtra, (aRow) => aRow.cif.WB_TEVTSPRD_ID);
                        await aTPMEventSpend.removeTPMEventSpendProductRow(arrExtraIds);
                    }
                }

                //update event spend products pricing
                if (
                    aTPMEventSpend.TPMEventSpendRow.cif.SHIPMENT_START_DT ==
                    aTPMEventBO.WB_TPMEvent.cif.SHIPMENT_START_DT
                ) {
                    _.forEach(aTPMEventSpend.TPMEventSpendRow.WB_TPM_EventSpendPrdRows, (aSpendPrdRow) => {
                        let prdId: number = aSpendPrdRow.cif.WB_PROD_ID;
                        let evtPrdRow: WB_TPM_EventProductRow = dctEventProductsByProdId[prdId];

                        if (_.isUndefined(evtPrdRow) || _.isNull(evtPrdRow)) return;

                        aSpendPrdRow.cif.NET_LIST_PRICE = evtPrdRow.cif.NET_LIST_PRICE;
                        aSpendPrdRow.cif.GROSS_LIST_PRICE = evtPrdRow.cif.GROSS_LIST_PRICE;
                    });
                } else {
                    await self._Price_EventSpend_Products(
                        aTPMEventBO,
                        aTPMEventSpend.TPMEventSpendRow.WB_TPM_EventSpendPrdRows,
                    );
                }
            }

            RunSpendProductSync()
                .then((ignore) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    },
};

export { EventStandardCalculatorExtDefault };
